import { clsx } from "clsx";
import { type ComponentProps } from "react";
import styling from "./FlexAligner.module.scss";
import {
	type AlignItems,
	type Alignment,
	type Direction,
	type FlexDisplay,
	type FlexSpacing,
	type JustifyContent,
	type FlexWrap,
} from "src/models/types/common.types";
import { type Merge } from "src/models/utility";
import { FlexColumnAlignment, FlexRowAlignment } from "src/models/records";

export type FlexAlignerProps = Merge<
	Omit<ComponentProps<"div">, "color" | "content">,
	{
		display?: FlexDisplay;
		alignment?: Alignment;
		direction?: Direction;
		spacing?: FlexSpacing;
		width?: "auto" | "stretch" | "viewport";
		height?: "auto" | "stretch" | "viewport";
		spacebetween?: boolean;
		color?: string;
		tag?: "div" | "nav" | "section" | "header" | "main" | "footer";
		wrap?: FlexWrap;
		ref?: React.Ref<HTMLDivElement>;
	}
>;

export const FlexAligner = (props: FlexAlignerProps) => {
	const {
		ref,
		id,
		className,
		children,
		tag = "div",
		width,
		height,
		display = "flex",
		alignment = "top-left",
		direction = "row",
		wrap = "nowrap",
		spacing,
		color,
		spacebetween = false,
		style,
		...rest
	} = props;

	const _simpleFlexGridAlignment = (): { content: JustifyContent; items: AlignItems } => {
		let simplifiedAlignment: { content: JustifyContent; items: AlignItems } = {
			content: "flex-start",
			items: "flex-start",
		};

		if (direction === "column") {
			simplifiedAlignment = FlexColumnAlignment[alignment];
		} else {
			simplifiedAlignment = FlexRowAlignment[alignment];
		}

		return simplifiedAlignment;
	};

	const _configuredProps = () => {
		return {
			id,
			className: clsx(styling["wrapper"], {
				[styling[`display--${display}`]]: display,
				[styling[`direction--${direction}`]]: direction,
				[styling[`align-items--${_simpleFlexGridAlignment().items}`]]: alignment && direction,
				[styling[`justify-content--${_simpleFlexGridAlignment().content}`]]: alignment && direction,
				[styling[`spacing--${spacing}`]]: spacing,
				[styling[`spacebetween`]]: spacebetween,
				[styling[`width--${width}`]]: width,
				[styling[`height--${height}`]]: height,
				[styling[`wrap--${wrap}`]]: wrap,
				[`${className}`]: className,
			}),
			style: { ...style, ...{ backgroundColor: color } },
		};
	};

	switch (tag) {
		case "nav":
			return (
				<nav {..._configuredProps()} ref={ref} {...rest}>
					{children}
				</nav>
			);
		case "section":
			return (
				<section {..._configuredProps()} ref={ref} {...rest}>
					{children}
				</section>
			);
		case "header":
			return (
				<header {..._configuredProps()} ref={ref} {...rest}>
					{children}
				</header>
			);
		case "main":
			return (
				<main {..._configuredProps()} ref={ref} {...rest}>
					{children}
				</main>
			);
		case "footer":
			return (
				<footer {..._configuredProps()} ref={ref} {...rest}>
					{children}
				</footer>
			);
		default:
			return (
				<div {..._configuredProps()} ref={ref} {...rest}>
					{children}
				</div>
			);
	}
};
